import { useEventListener } from "@vueuse/core";
import { computed, onMounted, ref, toValue, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";

/**
 * Bind to a native <select> by id, mirror its options/value into refs, and
 * provide an apply() that writes back to the source select and dispatches a
 * bubbling change event on its form. The host's live `value` property is the select's value, and a
 * pick also dispatches a bubbling `input` event on the host.
 *
 * @param {import("vue").MaybeRefOrGetter<string>} selectIdRef
 */
export function useSortSelect(selectIdRef, { exposeValue = true } = {}) {
  const { emitBubbling } = useHostEmit();
  const { exposeProperties } = useExpose();
  const host = useHostElement();
  const sourceSelect = ref(null);
  const options = ref([]);
  const selectedValue = ref("");
  const pendingValue = ref(null);

  const isBound = computed(() => Boolean(sourceSelect.value));
  const sourceForm = computed(() => sourceSelect.value?.form);

  function findSourceSelect() {
    return document.getElementById(toValue(selectIdRef));
  }

  function bindSourceSelect() {
    sourceSelect.value = findSourceSelect();
    applyPendingValue();
    sync();
  }

  function applyPendingValue() {
    if (sourceSelect.value && pendingValue.value !== null) {
      setSourceValue(pendingValue.value);
      pendingValue.value = null;
    }
  }

  function setSourceValue(value) {
    const source = sourceSelect.value;
    source.value = value;
    if (source.selectedIndex < 0) {
      source.selectedIndex = 0;
    }
  }

  function sync() {
    const source = sourceSelect.value;
    if (!source) {
      options.value = [];
      selectedValue.value = "";
      return;
    }

    const allOptions = Array.from(source.options).map((option) => ({
      label: option.textContent?.trim() || "",
      value: option.value,
      hint: option.dataset.sortHint || "",
    }));
    options.value = allOptions;

    const defaultValue = options.value[0]?.value || "";
    selectedValue.value = source.selectedIndex >= 0 ? source.value : defaultValue;
  }

  function apply(value) {
    const source = sourceSelect.value;
    if (!source || value == null) {
      return false;
    }

    if (source.value !== value) {
      source.value = value;
      selectedValue.value = value;
      emitBubbling("input", value);
      emitBubbling("change", value);
      if (!source.form?.contains(host)) {
        source.form?.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return true;
    }

    return false;
  }

  function setValue(value) {
    const normalizedValue = String(value ?? "");
    if (sourceSelect.value) {
      setSourceValue(normalizedValue);
      sync();
    } else {
      pendingValue.value = normalizedValue;
    }
  }

  function reset() {
    sync();
  }

  function syncAfterSourceFormReset() {
    // Native reset events fire before their controls adopt default values.
    queueMicrotask(sync);
  }

  if (exposeValue) {
    exposeProperties({
      value: {
        get: () => selectedValue.value,
        set: setValue,
      },
    });
  }

  useEventListener(sourceSelect, "change", sync);
  useEventListener(sourceForm, "reset", syncAfterSourceFormReset);

  watch(selectIdRef, bindSourceSelect);

  onMounted(bindSourceSelect);

  return { sourceSelect, options, selectedValue, isBound, apply, reset, sync, setValue };
}
